import { Partner } from "@justhome/api-core";
import useIsTest from "@lib/hooks/useIsTest";
import useLanguage from "@lib/hooks/useLanguage";
import clsx from "clsx";
import Image from "next/image";
import React, { PropsWithChildren } from "react";
import Link from "../Link";

// A document navigation, not `next/link`: the German homepage is served by the App Router (see
// `src/middleware.ts`) while every page this logo appears on belongs to the Pages Router, and
// Next cannot transition between the two on the client. It resolves the rewrite against the
// Pages Router route list, finds no page behind it and renders `_error` instead.
const HomeLink: React.FC<PropsWithChildren> = ({ children }) => {
  const locale = useLanguage();

  return (
    <a
      href={locale === "de" ? "/" : `/${locale}`}
      className="focusable rounded-xs"
    >
      {children}
    </a>
  );
};

const NavBarLogo: React.FC<{
  partner: Partner | undefined;
  className?: string;
}> = ({ partner, className }) => {
  const isTest = useIsTest();
  const logo = isTest ? "logo-test" : "logo-new";
  const logoSrc = `/assets/img/${logo}.svg`;

  return (
    <div
      className={clsx(
        "hover:cursor-pointer flex flex-none items-center",
        partner ? "h-[28px] m:h-2m" : "h-m",
        className,
      )}
    >
      {partner?.logoUrl && (
        <div className="flex items-center gap-2xs">
          <Link href={partner?.returnUrl}>
            <Image
              className="h-m m:h-2m object-contain w-auto max-w-[120px] m:max-w-[148px]"
              width="148"
              height="40"
              loading="eager"
              priority
              src={partner.logoUrl}
              alt=""
            />
          </Link>
          <span className="border-r border-neutral-100 h-3s"></span>
          <HomeLink>
            <Image
              height="24"
              width="102"
              loading="eager"
              priority
              src="/assets/img/logo-new.svg"
              className="w-auto h-2s m:h-3s"
              alt="Justhome"
            />
          </HomeLink>
        </div>
      )}
      {!partner && (
        <HomeLink>
          <Image
            width="170"
            height="40"
            src={logoSrc}
            loading="eager"
            priority
            alt="Justhome"
            className="object-contain object-left h-m w-auto"
          />
        </HomeLink>
      )}
    </div>
  );
};

export default NavBarLogo;
